// Admin PaymentsPage — revenue overview plus the payment ledger, where an admin verifies the
// transaction codes students submit. Defaults to payments still awaiting verification.
import { useState } from "react";
import { useFetch } from "../../hooks/useApi.js";
import StatCard from "../../components/shared/StatCard.js";
import TransactionStatusBadge from "../../components/shared/TransactionStatusBadge.js";
import VerifyPaymentForm from "../../components/admin/VerifyPaymentForm.js";
import ProofSummary from "../../components/admin/ProofSummary.js";
import { MAX_PAGE_SIZE } from "@dm-lms/shared/constants";
import type { AdminPayment, AdminSnapshot, PaginatedResult, PaymentWithProof } from "@dm-lms/shared/types";

type LedgerFilter = "pending" | "all";

const FILTERS: { value: LedgerFilter; title: string }[] = [
  { value: "pending", title: "Awaiting verification" },
  { value: "all",     title: "All payments" },
];

export default function PaymentsPage() {
  const snapshot = useFetch<AdminSnapshot>("/api/admin/snapshot");
  const [filter, setFilter] = useState<LedgerFilter>("pending");
  const ledger = useFetch<PaginatedResult<AdminPayment>>(
    `/api/payments?page=1&pageSize=${MAX_PAGE_SIZE}${filter === "pending" ? "&status=pending" : ""}`
  );
  // Rows the admin just settled, kept so their badge updates in place instead of the row vanishing
  // from the "pending" list mid-action.
  const [settled, setSettled] = useState<Record<string, PaymentWithProof>>({});

  function handleSettled(payment: PaymentWithProof) {
    setSettled((prev) => ({ ...prev, [payment.id]: payment }));
    snapshot.refresh();   // pending count and revenue cards
  }

  const rows = ledger.data?.items ?? [];

  return (
    <div className="dm-page">
      <h1 className="text-2xl font-semibold text-dm-navy mb-6">Payments</h1>

      {snapshot.isLoading && <p className="text-dm-slate text-sm">Loading payments overview…</p>}
      {snapshot.error && <p className="text-red-600 text-sm">{snapshot.error}</p>}

      {snapshot.data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <StatCard label="Total Revenue (KES)" value={snapshot.data.totalRevenueKes.toLocaleString()} />
          <StatCard label="Pending Payments" value={snapshot.data.pendingPayments} />
          <StatCard label="Total Enrollments" value={snapshot.data.totalEnrollments} />
        </div>
      )}

      <div role="tablist" className="mb-4 flex flex-wrap gap-1 border-b border-gray-200">
        {FILTERS.map((option) => (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={option.value === filter}
            onClick={() => {
              setFilter(option.value);
              setSettled({});
            }}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition ${
              option.value === filter
                ? "border-dm-navy text-dm-navy"
                : "border-transparent text-dm-slate hover:text-dm-navy"
            }`}
          >
            {option.title}
          </button>
        ))}
      </div>

      {ledger.isLoading && <p className="text-dm-slate text-sm">Loading payments…</p>}
      {ledger.error && <p className="text-red-600 text-sm">{ledger.error}</p>}

      {!ledger.isLoading && !ledger.error && (
        rows.length === 0 ? (
          <p className="text-dm-slate text-sm">
            {filter === "pending" ? "No payments are awaiting verification." : "No payments yet."}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-dm-slate">
                  <tr>
                    <th className="px-4 py-3 font-medium">Code</th>
                    <th className="px-4 py-3 font-medium">Student</th>
                    <th className="px-4 py-3 font-medium">Course</th>
                    <th className="px-4 py-3 font-medium">Amount (KES)</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((item) => {
                    const row: AdminPayment = { ...item, ...settled[item.id] };
                    return (
                      <tr key={row.id} className="align-top">
                        <td className="px-4 py-3 text-dm-navy font-medium">{row.code}</td>
                        <td className="px-4 py-3">{row.studentCode}</td>
                        <td className="px-4 py-3">{row.courseTitle ?? "—"}</td>
                        <td className="px-4 py-3">{row.amountKes.toLocaleString()}</td>
                        <td className="px-4 py-3">
                          <TransactionStatusBadge status={row.status} hasReference={row.reference !== null} />
                          <ProofSummary payment={row} />
                        </td>
                        <td className="px-4 py-3">
                          <VerifyPaymentForm payment={row} onSettled={handleSettled} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {ledger.data?.hasMore && (
              <p className="mt-2 text-xs text-dm-slate">Showing the latest {MAX_PAGE_SIZE} payments.</p>
            )}
          </>
        )
      )}
    </div>
  );
}
